import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  HelpCircle, Mic, Search, Bot, ArrowRight, CheckCircle2,
  Zap, Globe, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const WhatIsAEOPage = () => (
  <div className="min-h-screen pt-28 pb-20" data-testid="what-is-aeo-page">
    <Helmet>
      <title>What Is AEO? Answer Engine Optimization Explained (2026) | Digital Media Ninja</title>
      <meta name="description" content="Answer Engine Optimization (AEO) is the practice of optimizing content to appear as direct answers in featured snippets, voice search, and AI summaries. Complete guide." />
      <link rel="canonical" href="https://digitalmedianinja.com/what-is-aeo/" />
    </Helmet>

    <article className="container-custom max-w-4xl mx-auto px-4" data-speakable="true">
      <div className="mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
          <HelpCircle className="w-4 h-4" /> Definition Guide
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">What Is Answer Engine Optimization (AEO)?</h1>
        <p className="text-lg text-gray-300 leading-relaxed mb-6" data-speakable="true">
          <strong className="text-white">Answer Engine Optimization (AEO)</strong> is the practice of optimizing your digital content to appear as the direct answer to user questions in featured snippets, People Also Ask (PAA) boxes, voice search results, and AI-generated summaries. Unlike traditional SEO which focuses on ranking web pages, AEO focuses on making your content the <em>answer</em> — the concise, authoritative response that search engines and AI assistants serve directly to users.
        </p>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">How AEO Works</h2>
        <div className="space-y-4">
          {[
            { icon: Search, title: "Featured Snippet Optimization", desc: "AEO targets the \"position zero\" result — the answer box that appears above all organic results. This requires structuring content in the exact format Google prefers: definition paragraphs (40-60 words), numbered lists, or comparison tables." },
            { icon: HelpCircle, title: "People Also Ask (PAA)", desc: "Google's PAA boxes expand into chains of related questions. AEO maps these question chains for your industry and creates content that captures multiple PAA positions per topic, multiplying your visibility." },
            { icon: Mic, title: "Voice Search Answers", desc: "When someone asks Siri, Alexa, or Google Assistant a question, the device reads a single answer aloud. That answer almost always comes from a featured snippet. AEO ensures your content is the one selected." },
            { icon: Bot, title: "AI Summary Citations", desc: "Google AI Overviews, ChatGPT, and Perplexity pull from authoritative sources when generating answers. AEO uses FAQ schema, SpeakableSpecification markup, and citation-optimized formatting to become that source." },
          ].map((item, i) => (
            <div key={i} className="flex gap-4 bg-white/5 border border-white/10 rounded-xl p-5">
              <item.icon className="w-6 h-6 text-emerald-400 shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">AEO vs. Traditional SEO</h2>
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {[
            ["Goal", "Rank web pages in blue links", "Become the direct spoken/displayed answer"],
            ["Primary Metric", "Organic rankings & clicks", "Featured snippet wins & voice answer rate"],
            ["Content Format", "Long-form keyword-optimized pages", "Concise Q&A pairs, definitions, structured data"],
            ["Schema Required", "Basic (Article, Breadcrumb)", "FAQ, HowTo, Speakable, Q&A"],
            ["Voice Search", "Not directly optimized", "Primary optimization target"],
          ].map(([aspect, seo, aeo], i) => (
            <div key={i} className="grid grid-cols-3 px-5 py-3 border-b border-white/5 last:border-0">
              <span className="text-white text-sm font-medium">{aspect}</span>
              <span className="text-gray-400 text-sm">{seo}</span>
              <span className="text-emerald-300 text-sm">{aeo}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Key AEO Statistics (2026)</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
            <div className="text-3xl font-bold text-emerald-400 mb-2">8.6%</div>
            <p className="text-gray-400 text-sm">of all clicks go to featured snippets</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
            <div className="text-3xl font-bold text-emerald-400 mb-2">70%</div>
            <p className="text-gray-400 text-sm">of voice search answers come from snippets</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
            <div className="text-3xl font-bold text-emerald-400 mb-2">55%</div>
            <p className="text-gray-400 text-sm">of households own a smart speaker</p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: "Is AEO replacing SEO?", a: "No. AEO is an extension of SEO, not a replacement. You still need strong organic rankings and domain authority — AEO builds on that foundation to capture answer-format visibility." },
            { q: "What tools are used for AEO?", a: "AEO uses schema markup validators (Google Rich Results Test), PAA research tools (AlsoAsked, AnswerThePublic), voice search testing, and AI citation monitoring tools." },
            { q: "How quickly does AEO produce results?", a: "FAQ schema and snippet optimizations can produce results in 2-6 weeks. Voice search improvements take 1-3 months. Full PAA domination is a 3-6 month strategy." },
          ].map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white/5 border border-white/10 rounded-xl px-5">
              <AccordionTrigger className="text-white text-left text-sm font-medium hover:no-underline">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-gray-400 text-sm">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Cross-links */}
      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        <Link to="/what-is-geo" className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 hover:bg-purple-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is GEO?</h3>
          <p className="text-gray-400 text-xs">Generative Engine Optimization</p>
        </Link>
        <Link to="/what-is-llmo" className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 hover:bg-blue-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is LLMO?</h3>
          <p className="text-gray-400 text-xs">LLM Optimization</p>
        </Link>
        <Link to="/aeo-vs-geo-vs-seo" className="bg-[#2ABFBF]/5 border border-[#2ABFBF]/20 rounded-xl p-4 hover:bg-[#2ABFBF]/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">AEO vs GEO vs SEO</h3>
          <p className="text-gray-400 text-xs">Full comparison guide</p>
        </Link>
      </div>

      <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">Want AEO for Your Business?</h2>
        <p className="text-gray-400 mb-6">See how your content performs across featured snippets, PAA, and voice search.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/services/aeo"><Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold">AEO Services</Button></Link>
          <Link to="/free-ai-visibility-audit"><Button variant="outline" className="border-white/20 text-gray-300">Free AI Visibility Audit</Button></Link>
        </div>
      </div>
    </article>
  </div>
);

export default WhatIsAEOPage;
